import { useState } from "react";
import TextField from "@mui/material/TextField";
import { DateTimePicker } from "@mui/x-date-pickers/DateTimePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs from "dayjs";
import * as yup from "yup";
import { useFormik } from "formik";
import Select from "@mui/material/Select";
import OutlinedInput from "@mui/material/OutlinedInput";
import MenuItem from "@mui/material/MenuItem";
import { BsArrowDownCircle, BsArrowUpCircle } from "react-icons/bs";
import { HashLoader } from "react-spinners";
import Checkbox from "@mui/material/Checkbox";
import FormGroup from "@mui/material/FormGroup";
import FormControlLabel from "@mui/material/FormControlLabel";

import { MainButton } from "../../components/MainButton";
import { COLORS } from "../../utils/colors";
import { ICONS } from "../../utils/icons";
import { updateEvent } from "../../api/events";

const MenuProps = {
  PaperProps: {
    style: {
      maxHeight: 250,
      minWidth: 250,
      fontFamily: "AuthenticSans90",
    },
  },
};

const validationSchema = yup.object({
  name: yup.string("Must be string").required("Required"),
  capacity: yup.number("Must be number").required("Required"),
  max_purchase_amount: yup.number("Must be number").required("Required"),
  start_date: yup.date().required("Required"),
  end_date: yup.date().required("Required"),
  max_purchase_amount: yup.number().required("Required"),
  terms: yup
    .string("Must be string")
    .url("Must be a valid URL. Example: https://example.com"),
  privacy_policy: yup
    .string("Must be string")
    .url("Must be a valid URL. Example: https://example.com"),
  refund_policy: yup
    .string("Must be string")
    .url("Must be a valid URL. Example: https://example.com"),
});

const requiredFields1 = [
  {
    label: "Event Name",
    name: "name",
    type: "text",
    required: true,
  },
  {
    label: "Maximum Capacity",
    name: "capacity",
    type: "number",
    required: true,
  },
  {
    label: "Maximum Items per Purchase",
    name: "max_purchase_amount",
    type: "number",
    required: true,
  },
];

const requiredFields2 = [
  {
    label: "Start Date & Time",
    name: "start_date",
    type: "date",
    required: true,
  },
  {
    label: "End Date & Time",
    name: "end_date",
    type: "date",
    required: true,
  },
];

const optionalFields1 = [
  {
    label: "Description",
    name: "description",
    type: "text",
  },
  {
    label: "Currency",
    name: "currency",
    type: "text",
  },
  {
    label: "Overbooking Enabled",
    name: "overbooking",
    type: "singleSelect",
    options: [
      {
        label: "Yes",
        value: true,
      },
      {
        label: "No",
        value: false,
      },
    ],
  },
  {
    label: "Status",
    name: "status",
    type: "singleSelect",
    options: [
      {
        label: "Active",
        value: "active",
      },
      {
        label: "Inactive",
        value: "inactive",
      },
    ],
  },
  {
    label: "Ticket Types",
    name: "type",
    type: "singleSelect",
    options: [
      {
        label: "General Admission",
        value: "general_admission",
      },
      // {
      //   label: "Seated Event",
      //   value: "seated_event",
      // },
      // {
      //   label: "Combined",
      //   value: "combined",
      // },
    ],
  },
  {
    label: "Gallery",
    name: "gallery",
    type: "file",
    multiple: true,
  },
];

const optionalFields2 = [
  {
    label: "Motto",
    name: "motto",
    type: "text",
  },
  {
    label: "Sale Start",
    name: "sale_start",
    type: "date",
  },

  {
    label: "Sale End",
    name: "sale_end",
    type: "date",
  },
  {
    label: "Checkin Start",
    name: "checkin_start",
    type: "date",
  },
  {
    label: "Checkin End",
    name: "checkin_end",
    type: "date",
  },
  {
    label: "Banner Image",
    name: "banner",
    type: "file",
  },
];

export const Overview = ({
  event,
  setEvent,
  handleSnackbar,
  supabase,
  userData,
}) => {
  const [loading, setLoading] = useState(false);
  const [showOptionalFields, setShowOptionalFields] = useState(false);
  const [banner, setBanner] = useState(event?.banner);
  const [gallery, setGallery] = useState(event?.gallery);
  const [previewImages, setPreviewImages] = useState({
    banner: true,
    gallery: true,
  });
  const [extraFields, setExtraFields] = useState(
    event.extra_fields
      ? Object.entries(event.extra_fields).map(([name, value]) => ({
          name,
          value,
        }))
      : []
  );
  const [pwaImages, setPwaImages] = useState(event.pwa_images_block);

  const formik = useFormik({
    initialValues: {
      name: event.name,
      capacity: event.capacity,
      max_purchase_amount: event.max_purchase_amount,
      start_date: dayjs(event.start_date),
      end_date: dayjs(event.end_date),
      type: event.type,
      description: event.description || "",
      currency: event.currency,
      overbooking: event.overbooking || false,
      status: event.status || "active",
      whitelist: event.whitelist || null,
      motto: event.motto || "",
      sale_start: dayjs(event.sale_start),
      sale_end: dayjs(event.sale_end),
      checkin_start: dayjs(event.checkin_start),
      checkin_end: dayjs(event.checkin_end),
      terms: event.terms || "",
      privacy_policy: event.privacy_policy || "",
      refund_policy: event.refund_policy || "",
      form_questions_anonymous: event.form_questions_anonymous || false,
      pwa_images_block: event.pwa_images_block || false,
      pwa_images_block_title: event.pwa_images_block_title || "",
      show_pwa_images_block: event.show_pwa_images_block || false,
    },
    validationSchema: validationSchema,
    onSubmit: (values) => handleFormSubmit(values),
  });

  const removeBanner = async () => {
    setBanner(null);
    formik.values["banner"] = null;
  };

  const removeGallery = async () => {
    setGallery(null);
    formik.values["gallery"] = null;
  };

  const handleReset = () => {
    formik.setValues({
      name: event.name,
      capacity: event.capacity,
      max_purchase_amount: event.max_purchase_amount,
      start_date: dayjs(event.start_date),
      end_date: dayjs(event.end_date),
      type: event.type || null,
      description: event.description,
      currency: event.currency,
      overbooking: event.overbooking,
      status: event.status,
      whitelist: event.whitelist,
      gallery: event.gallery,
      motto: event.motto,
      sale_start: dayjs(event.sale_start),
      sale_end: dayjs(event.sale_end),
      checkin_start: dayjs(event.checkin_start),
      checkin_end: dayjs(event.checkin_end),
      banner: event.banner,
      form_questions_anonymous: event.form_questions_anonymous,
      extra_fields: event.extra_fields,
      pwa_images_block: event.pwa_images_block,
      pwa_images_block_title: event.pwa_images_block_title,
      show_pwa_images_block: event.show_pwa_images_block,
    });

    setExtraFields(
      event.extra_fields
        ? Object.entries(event.extra_fields).map(([name, value]) => ({
            name,
            value,
          }))
        : []
    );
  };

  const handleFormSubmit = async (values) => {
    console.log("Updating event...");

    try {
      setLoading(true);

      if (banner && banner !== event.banner) {
        const bannerUrl = await uploadImage(banner);
        values["banner"] = bannerUrl[0];
        setBanner(bannerUrl[0]);
      }

      if (gallery && gallery !== event.gallery) {
        const galleryUrls = await uploadImage(gallery);
        values["gallery"] = galleryUrls;
        setGallery(galleryUrls);
      }

      if (extraFields.length) {
        const extraFieldsObject = extraFields.reduce((acc, item) => {
          if (item.name === "" || item.value === "") {
            handleSnackbar("Please fill in all extra fields", "error");

            throw new Error("Please fill in all extra fields");
          }

          acc[item.name] = item.value;
          return acc;
        }, {});

        values["extra_fields"] = extraFieldsObject;
      } else {
        values["extra_fields"] = {};
      }

      if (
        pwaImages.length !== 0 &&
        pwaImages[pwaImages.length - 1].previewUrl === ""
      ) {
        throw new Error("The last PWA image is not set");
      }

      if (pwaImages.length !== 0 && pwaImages !== event.pwa_images_block) {
        const updatedPwaImages = await Promise.all(
          pwaImages.map(async (image, index) => {
            if (
              !event.pwa_images_block[index] ||
              image.url !== event.pwa_images_block[index].url
            ) {
              if (typeof image.url !== "string") {
                const imageUrl = await uploadImage(image.url);
                return { ...image, url: imageUrl[0], previewUrl: imageUrl[0] };
              } else {
                return image;
              }
            }
            return image;
          })
        );
        console.log("Updated PWA images: ", updatedPwaImages);
        setPwaImages(updatedPwaImages);
        values["pwa_images_block"] = updatedPwaImages;
      } else if (pwaImages.length === 0) {
        values["pwa_images_block"] = [];
      }

      const { data, error } = await updateEvent(values, event);

      if (error) {
        handleSnackbar(error.message, "error");
        return;
      }

      if (data) {
        setEvent(data);
        handleSnackbar("Success!", "success");
      }

      setLoading(false);
    } catch (err) {
      handleSnackbar(
        "Please check all fields & contact us if it persists. " + err,
        "error"
      );
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    console.log("Deleting event...");
  };

  const handleSelectedPwaImage = (event, givenIndex) => {
    const newImages = [...pwaImages];
    newImages[givenIndex] = {
      title: newImages[givenIndex].title,
      url: event.target.files[0],
      previewUrl: URL.createObjectURL(event.target.files[0]),
    };
    setPwaImages(newImages);
  };

  const handleSelectedFile = (event) => {
    setBanner(event.target.files[0]);
    setPreviewImages({ ...previewImages, banner: false });
  };

  const handleSelectedGallery = (event) => {
    setGallery(event.target.files);
    setPreviewImages({ ...previewImages, gallery: false });
  };

  const uploadImage = async (incomingFiles) => {
    try {
      setLoading(true);

      let filePaths = [];

      if (incomingFiles?.length) {
        for (let i = 0; i < incomingFiles.length; i++) {
          const file = incomingFiles[i];

          const { data, error } = await supabase.storage
            .from("ooval-bucket")
            .upload(
              `organization_folders/${
                userData?.email.split("@")[1].split(".")[0]
              }/${file.name}`,
              file,
              {
                upsert: true,
              }
            );

          if (error) {
            console.log(error);
            handleSnackbar(error.message, "error");
            setLoading(false);
            return;
          }

          const fileUrl = await getPublicUrl(data.path);
          filePaths.push(fileUrl);
        }
      } else {
        const { data, error } = await supabase.storage
          .from("ooval-bucket")
          .upload(
            `organization_folders/${
              userData?.email.split("@")[1].split(".")[0]
            }/${incomingFiles.name}`,
            incomingFiles,
            {
              upsert: true,
            }
          );

        if (error) {
          console.log(error);
          handleSnackbar(error.message, "error");
          setLoading(false);
          return;
        }

        const fileUrl = await getPublicUrl(data.path);
        filePaths.push(fileUrl);

        setLoading(false);
      }

      return filePaths;
    } catch (err) {
      console.log(err);
      handleSnackbar(err.message, "error");
      setLoading(false);
    }
  };

  const getPublicUrl = async (path) => {
    const { data, error } = supabase.storage
      .from("ooval-bucket")
      .getPublicUrl(`${path}`);

    if (error) {
      console.log(error);
      return;
    }

    return data.publicUrl;
  };

  const addOption = async () => {
    if (
      extraFields[extraFields.length - 1]?.name === "" ||
      extraFields[extraFields.length - 1]?.value === ""
    )
      return handleSnackbar("Please fill in the previous extra field", "error");

    setExtraFields([...extraFields, { name: "", value: "" }]);
  };

  const addPwaImage = async () => {
    if (pwaImages.length === 0) {
      setPwaImages([{ title: "", url: "", previewUrl: "" }]);
      return;
    }

    if (pwaImages[pwaImages.length - 1]?.url === "")
      return handleSnackbar("Previous image is not set", "error");

    setPwaImages([...pwaImages, { title: "", url: "", previewUrl: "" }]);
  };

  const deletePwaImage = async (givenIndex) => {
    const newImages = pwaImages.filter((item, index) => index !== givenIndex);
    setPwaImages(newImages);
  };

  const deleteOption = async (givenIndex) => {
    const newFields = extraFields.filter((item, index) => index !== givenIndex);
    setExtraFields(newFields);
  };

  const handleTextFieldChange = (index, event, field) => {
    const newValues = [...extraFields];

    if (field === "name") {
      newValues[index] = { ...newValues[index], name: event.target.value };
    }

    if (field === "value") {
      newValues[index] = { ...newValues[index], value: event.target.value };
    }

    setExtraFields(newValues);
  };

  return (
    <>
      <div style={{ height: "20px" }} />
      <div className="standard-container">
        <div>
          <div
            className="container-row"
            style={{ alignItems: "center", justifyContent: "space-between" }}
          >
            <div>
              <h2>Update event information</h2>
              <p id="subTitle">Update the event from here.</p>
            </div>
          </div>
          <form
            className="container-column"
            style={{ margin: "40px 0px 10px 0" }}
          >
            <LocalizationProvider dateAdapter={AdapterDayjs}>
              {/* <div className="two-columns-fields-container"> */}
              <div style={{ width: "-webkit-fill-available" }}>
                {requiredFields1.map((field, index) => {
                  return (
                    <div key={index}>
                      {field.type === "text" && (
                        <div className="field-container">
                          <p
                            id="subheader"
                            style={{
                              margin: "0px 0px 3px",
                              alignSelf: "center",
                            }}
                          >
                            {field.label}*
                          </p>
                          <TextField
                            fullWidth
                            disabled={loading}
                            size="small"
                            name={field.name}
                            placeholder="not set"
                            value={formik.values[field.name]}
                            onChange={formik.handleChange}
                            error={
                              formik.touched[field.name] &&
                              Boolean(formik.errors[field.name])
                            }
                            helperText={
                              formik.touched[field.name] &&
                              formik.errors[field.name]
                            }
                            sx={{
                              "& label": {
                                paddingLeft: (theme) => theme.spacing(1),
                              },
                              "& fieldset": {
                                borderRadius: "6px",
                                border: `2px solid #f0f1f5`,
                                transition: ".2s ease-in-out all",
                              },
                              "& .MuiOutlinedInput-root.Mui-disabled .MuiOutlinedInput-notchedOutline":
                                {
                                  borderColor: "#f0f1f5",
                                },
                              "& .MuiOutlinedInput-input:hover": {
                                cursor: loading && "not-allowed",
                              },
                            }}
                          />
                        </div>
                      )}
                      {field.type === "number" && (
                        <div className="field-container">
                          <p
                            id="subheader"
                            style={{
                              margin: "0px 0px 3px",
                              alignSelf: "center",
                            }}
                          >
                            {field.label}*
                          </p>
                          <TextField
                            fullWidth
                            type="number"
                            inputProps={{ min: 0 }}
                            disabled={loading}
                            size="small"
                            name={field.name}
                            placeholder="not set"
                            value={formik.values[field.name]}
                            onChange={formik.handleChange}
                            error={
                              formik.touched[field.name] &&
                              Boolean(formik.errors[field.name])
                            }
                            helperText={
                              formik.touched[field.name] &&
                              formik.errors[field.name]
                            }
                            sx={{
                              "& label": {
                                paddingLeft: (theme) => theme.spacing(1),
                              },
                              "& fieldset": {
                                borderRadius: "6px",
                                border: `2px solid #f0f1f5`,
                                transition: ".2s ease-in-out all",
                              },
                              "& .MuiOutlinedInput-root.Mui-disabled .MuiOutlinedInput-notchedOutline":
                                {
                                  borderColor: "#f0f1f5",
                                },
                              "& .MuiOutlinedInput-input:hover": {
                                cursor: loading && "not-allowed",
                              },
                            }}
                          />
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
              <div style={{ width: "-webkit-fill-available" }}>
                {requiredFields2.map((field, index) => {
                  return (
                    <div key={index}>
                      {field.type === "text" && (
                        <div className="field-container">
                          <p
                            id="subheader"
                            style={{
                              margin: "0px 0px 3px",
                              alignSelf: "center",
                            }}
                          >
                            {field.label}*
                          </p>
                          <TextField
                            fullWidth
                            disabled={loading}
                            size="small"
                            className="input"
                            name={field.name}
                            placeholder="not set"
                            value={formik.values[field.name]}
                            onChange={formik.handleChange}
                            error={
                              formik.touched[field.name] &&
                              Boolean(formik.errors[field.name])
                            }
                            helperText={
                              formik.touched[field.name] &&
                              formik.errors[field.name]
                            }
                            sx={{
                              "& label": {
                                paddingLeft: (theme) => theme.spacing(1),
                              },
                              "& fieldset": {
                                borderRadius: "6px",
                                border: `2px solid #f0f1f5`,
                                transition: ".2s ease-in-out all",
                              },
                              "& .MuiOutlinedInput-root.Mui-disabled .MuiOutlinedInput-notchedOutline":
                                {
                                  borderColor: "#f0f1f5",
                                },
                              "& .MuiOutlinedInput-input:hover": {
                                cursor: loading && "not-allowed",
                              },
                            }}
                          />
                        </div>
                      )}
                      {field.type === "date" && (
                        <div className="field-container">
                          <p
                            id="subheader"
                            style={{
                              margin: "0px 0px 3px",
                              alignSelf: "center",
                            }}
                          >
                            {field.label}*
                          </p>
                          <DateTimePicker
                            name="start_date"
                            value={formik.values[field.name]}
                            onChange={(newValue) =>
                              formik.setValues({
                                ...formik.values,
                                [field.name]: newValue,
                              })
                            }
                            minDate={dayjs(new Date())}
                            error={
                              formik.touched[field.name] &&
                              Boolean(formik.errors[field.name])
                            }
                            helperText={
                              formik.touched[field.name] &&
                              formik.errors[field.name]
                            }
                            renderInput={(params) => (
                              <TextField
                                size="small"
                                name="start_date"
                                {...params}
                                className={"text-field"}
                                error={
                                  formik.touched[field.name] &&
                                  Boolean(formik.errors[field.name])
                                }
                                helperText={
                                  formik.touched[field.name] &&
                                  formik.errors[field.name]
                                }
                                sx={{
                                  width: "-webkit-fill-available",
                                  "& fieldset": {
                                    border: "2px solid",
                                    borderRadius: "6px",
                                    borderColor: COLORS.background,
                                    height: "43px",
                                  },
                                  "& input": {
                                    height: "29px",
                                    borderRadius: "6px",
                                    padding: "5.5px 14px",
                                  },
                                  "&:hover fieldset": {
                                    transition: "0.2s",
                                  },
                                }}
                              />
                            )}
                          />
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>

              {showOptionalFields ? (
                <p
                  style={{
                    width: "-webkit-fill-available",
                    margin: "20px 0px",
                    cursor: "pointer",
                    alignItems: "center",
                    display: "flex",
                    gap: "5px",
                  }}
                  onClick={() => setShowOptionalFields(!showOptionalFields)}
                >
                  <BsArrowUpCircle size={18} /> Hide Optional Fields
                </p>
              ) : (
                <p
                  style={{
                    width: "-webkit-fill-available",
                    margin: "20px 0px",
                    cursor: "pointer",
                    alignItems: "center",
                    display: "flex",
                    gap: "5px",
                  }}
                  onClick={() => setShowOptionalFields(!showOptionalFields)}
                >
                  <BsArrowDownCircle size={18} /> Show Optional Fields
                </p>
              )}
              {showOptionalFields && (
                <>
                  <FormGroup style={{ marginBottom: "20px" }}>
                    <FormControlLabel
                      sx={{
                        "& .MuiTypography-root": {
                          fontFamily: "AuthenticSans90",
                        },
                      }}
                      control={
                        <Checkbox
                          name={"form_questions_anonymous"}
                          checked={formik.values.form_questions_anonymous}
                          onChange={
                            formik.values.form_questions_anonymous === false &&
                            formik.handleChange
                          }
                        />
                      }
                      label="Gather answers to form questions anonymously"
                    />
                    <p style={{ fontSize: "14px", color: "grey" }}>
                      WARNING: once turned on, this option cannot be turned off
                      anymore. If enabled, you will not be able to directly link
                      the ticket buyer to the answer.
                    </p>
                  </FormGroup>
                  <div className="two-columns-fields-container">
                    <div style={{ width: "-webkit-fill-available" }}>
                      {optionalFields1.map((field, index) => {
                        return (
                          <div key={index}>
                            {field.type === "text" && (
                              <div className="field-container">
                                <p
                                  id="subheader"
                                  style={{
                                    margin: "0px 0px 3px",
                                    alignSelf: "center",
                                  }}
                                >
                                  {field.label}
                                </p>
                                <TextField
                                  fullWidth
                                  disabled={loading}
                                  size="small"
                                  className="input"
                                  name={field.name}
                                  placeholder="not set"
                                  value={formik.values[field.name]}
                                  onChange={formik.handleChange}
                                  error={
                                    formik.touched[field.name] &&
                                    Boolean(formik.errors[field.name])
                                  }
                                  helperText={
                                    formik.touched[field.name] &&
                                    formik.errors[field.name]
                                  }
                                  sx={{
                                    "& label": {
                                      paddingLeft: (theme) => theme.spacing(1),
                                    },
                                    "& fieldset": {
                                      borderRadius: "6px",
                                      border: `2px solid #f0f1f5`,
                                      transition: ".2s ease-in-out all",
                                    },
                                    "& .MuiOutlinedInput-root.Mui-disabled .MuiOutlinedInput-notchedOutline":
                                      {
                                        borderColor: "#f0f1f5",
                                      },
                                    "& .MuiOutlinedInput-input:hover": {
                                      cursor: loading && "not-allowed",
                                    },
                                  }}
                                />
                              </div>
                            )}
                            {field.type === "date" && (
                              <div className="field-container">
                                <p
                                  id="subheader"
                                  style={{
                                    margin: "0px 0px 3px",
                                    alignSelf: "center",
                                  }}
                                >
                                  {field.label}
                                </p>
                                <DateTimePicker
                                  name="start_date"
                                  value={formik.values[field.name]}
                                  onChange={(newValue) =>
                                    formik.setValues({
                                      ...formik.values,
                                      [field.name]: newValue,
                                    })
                                  }
                                  minDate={dayjs(new Date())}
                                  renderInput={(params) => (
                                    <TextField
                                      size="small"
                                      name="startDate"
                                      {...params}
                                      className={"text-field"}
                                      sx={{
                                        "& fieldset": {
                                          border: "2px solid",
                                          borderRadius: "6px",
                                          borderColor: COLORS.background,
                                          height: "43px",
                                        },
                                        "& input": {
                                          height: "29px",
                                          borderRadius: "6px",
                                          padding: "5.5px 14px",
                                        },
                                        "&:hover fieldset": {
                                          transition: "0.2s",
                                        },
                                      }}
                                    />
                                  )}
                                />
                              </div>
                            )}
                            {field.type === "singleSelect" && (
                              <div className="field-container">
                                <p
                                  id="subheader"
                                  style={{
                                    margin: "0px 0px 3px",
                                    alignSelf: "center",
                                  }}
                                >
                                  {field.label}
                                </p>
                                <Select
                                  size="small"
                                  name={field.name}
                                  value={
                                    formik.values[field.name] === null
                                      ? ""
                                      : formik.values[field.name]
                                  }
                                  onChange={formik.handleChange}
                                  input={<OutlinedInput label="Name" />}
                                  MenuProps={MenuProps}
                                  style={{
                                    maxWidth: "100px",
                                    minWidth: "-webkit-fill-available",
                                  }}
                                  sx={{
                                    "& label": {
                                      paddingLeft: (theme) => theme.spacing(1),
                                    },
                                    "& fieldset": {
                                      borderRadius: "6px",
                                      border: `2px solid #f0f1f5`,
                                      transition: ".2s ease-in-out all",
                                    },
                                    "& .MuiOutlinedInput-root.Mui-disabled .MuiOutlinedInput-notchedOutline":
                                      {
                                        borderColor: "#f0f1f5",
                                      },
                                    "& .MuiOutlinedInput-input:hover": {
                                      cursor: loading && "not-allowed",
                                    },
                                  }}
                                >
                                  {field.options.map((option, index) => {
                                    return (
                                      <MenuItem
                                        value={option.value}
                                        key={index}
                                        style={{ minHeight: "40px" }}
                                      >
                                        {option.label}
                                      </MenuItem>
                                    );
                                  })}
                                </Select>
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                    <div style={{ width: "-webkit-fill-available" }}>
                      {optionalFields2.map((field, index) => {
                        return (
                          <div key={index}>
                            {field.type === "text" && (
                              <div className="field-container">
                                <p
                                  id="subheader"
                                  style={{
                                    margin: "0px 0px 3px",
                                    alignSelf: "center",
                                  }}
                                >
                                  {field.label}
                                </p>
                                <TextField
                                  fullWidth
                                  disabled={loading}
                                  size="small"
                                  className="input"
                                  name={field.name}
                                  placeholder="not set"
                                  value={formik.values[field.name]}
                                  onChange={formik.handleChange}
                                  error={
                                    formik.touched[field.name] &&
                                    Boolean(formik.errors[field.name])
                                  }
                                  helperText={
                                    formik.touched[field.name] &&
                                    formik.errors[field.name]
                                  }
                                  sx={{
                                    "& label": {
                                      paddingLeft: (theme) => theme.spacing(1),
                                    },
                                    "& fieldset": {
                                      borderRadius: "6px",
                                      border: `2px solid #f0f1f5`,
                                      transition: ".2s ease-in-out all",
                                    },
                                    "& .MuiOutlinedInput-root.Mui-disabled .MuiOutlinedInput-notchedOutline":
                                      {
                                        borderColor: "#f0f1f5",
                                      },
                                    "& .MuiOutlinedInput-input:hover": {
                                      cursor: loading && "not-allowed",
                                    },
                                  }}
                                />
                              </div>
                            )}
                            {field.type === "date" && (
                              <div className="field-container">
                                <p
                                  id="subheader"
                                  style={{
                                    margin: "0px 0px 3px",
                                    alignSelf: "center",
                                  }}
                                >
                                  {field.label}
                                </p>
                                <DateTimePicker
                                  name={field.name}
                                  value={formik.values[field.name]}
                                  onChange={(newValue) =>
                                    formik.setValues({
                                      ...formik.values,
                                      [field.name]: newValue,
                                    })
                                  }
                                  minDate={dayjs(new Date())}
                                  renderInput={(params) => (
                                    <TextField
                                      size="small"
                                      name="startDate"
                                      {...params}
                                      className={"text-field"}
                                      error={
                                        formik.touched[field.name] &&
                                        Boolean(formik.errors[field.name])
                                      }
                                      helperText={
                                        formik.touched[field.name] &&
                                        formik.errors[field.name]
                                      }
                                      sx={{
                                        "& fieldset": {
                                          border: "2px solid",
                                          borderRadius: "6px",
                                          borderColor: COLORS.background,
                                          height: "43px",
                                        },
                                        "& input": {
                                          height: "29px",
                                          borderRadius: "6px",
                                          padding: "5.5px 14px",
                                        },
                                        "&:hover fieldset": {
                                          transition: "0.2s",
                                        },
                                      }}
                                    />
                                  )}
                                />
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                  <div className="field-container">
                    <p
                      id="subheader"
                      style={{
                        margin: "0px 0px 3px",
                        alignSelf: "center",
                      }}
                    >
                      Gallery
                    </p>
                    {gallery && previewImages.gallery ? (
                      <div className="container-column">
                        <div className="container-column">
                          {gallery.map((image, index) => {
                            return (
                              <img
                                src={image}
                                key={index}
                                style={{
                                  width: "100%",
                                  height: "100px",
                                  objectFit: "cover",
                                  marginBottom: "10px",
                                }}
                              />
                            );
                          })}
                        </div>
                        <div
                          className="container-row"
                          style={{
                            width: "-webkit-fill-available",
                            justifyContent: "space-between",
                          }}
                        >
                          <input
                            multiple
                            type="file"
                            className="input-upload"
                            onChange={handleSelectedGallery}
                            placeholder="Upload logo"
                          />
                          <MainButton
                            text="Remove gallery images"
                            color={"transparent"}
                            textColor={COLORS.red}
                            textHoverColor={COLORS.lightRed}
                            hoverColor={"transparent"}
                            shadowColor={COLORS.red}
                            borderColor={COLORS.red}
                            onClickAction={() => removeGallery()}
                            width={"fit-content"}
                          />
                        </div>
                      </div>
                    ) : (
                      <div
                        className="container-row"
                        style={{
                          width: "-webkit-fill-available",
                          justifyContent: "space-between",
                        }}
                      >
                        <input
                          multiple
                          type="file"
                          className="input-upload"
                          onChange={handleSelectedGallery}
                          placeholder="Upload logo"
                        />
                        <MainButton
                          text="Remove gallery images"
                          color={"transparent"}
                          textColor={COLORS.red}
                          textHoverColor={COLORS.lightRed}
                          hoverColor={"transparent"}
                          shadowColor={COLORS.red}
                          borderColor={COLORS.red}
                          onClickAction={() => removeGallery()}
                          width={"fit-content"}
                        />
                      </div>
                    )}
                  </div>
                  <div className="field-container">
                    <p
                      id="subheader"
                      style={{
                        margin: "0px 0px 3px",
                        alignSelf: "center",
                      }}
                    >
                      Banner Image
                    </p>
                    {banner && previewImages.banner ? (
                      <div className="container-column">
                        <div className="container-row">
                          <img
                            src={banner}
                            style={{
                              height: "100px",
                              width: "100%",
                              objectFit: "cover",
                              marginBottom: "10px",
                            }}
                          />
                        </div>
                        <div
                          className="container-row"
                          style={{
                            width: "-webkit-fill-available",
                            justifyContent: "space-between",
                          }}
                        >
                          <input
                            type="file"
                            className="input-upload"
                            onChange={handleSelectedFile}
                            placeholder="Upload logo"
                          />
                          <MainButton
                            text="Remove banner"
                            color={"transparent"}
                            textColor={COLORS.red}
                            textHoverColor={COLORS.lightRed}
                            hoverColor={"transparent"}
                            shadowColor={COLORS.red}
                            borderColor={COLORS.red}
                            onClickAction={() => removeBanner()}
                            width={"fit-content"}
                          />
                        </div>
                      </div>
                    ) : (
                      <div
                        className="container-row"
                        style={{
                          width: "-webkit-fill-available",
                          justifyContent: "space-between",
                        }}
                      >
                        <input
                          type="file"
                          className="input-upload"
                          onChange={handleSelectedFile}
                          placeholder="Upload logo"
                        />
                        <MainButton
                          text="Remove banner"
                          color={"transparent"}
                          textColor={COLORS.red}
                          textHoverColor={COLORS.lightRed}
                          hoverColor={"transparent"}
                          shadowColor={COLORS.red}
                          borderColor={COLORS.red}
                          onClickAction={() => removeBanner()}
                          width={"fit-content"}
                        />
                      </div>
                    )}
                  </div>

                  <div
                    style={{
                      height: "1px",
                      maxHeight: "1px",
                      width: "100%",
                      backgroundColor: "lightgrey",
                      marginBottom: "60px",
                      marginTop: "40px",
                    }}
                  />

                  <div className="field-container">
                    <p
                      id="subheader"
                      style={{
                        margin: "0px 0px 3px",
                        alignSelf: "center",
                      }}
                    >
                      Extra Fields
                    </p>
                    <p
                      style={{
                        fontSize: "14px",
                        color: "grey",
                        marginTop: "10px",
                      }}
                    >
                      If you want to add a custom field, for example
                      "seat_cushion", you can do so by using the extra fields
                      section.
                    </p>
                    {extraFields.length !== 0 &&
                      extraFields.map((field, index) => {
                        return (
                          <div
                            key={index}
                            className="two-columns-fields-container"
                            style={{ margin: "5px 0" }}
                          >
                            <div style={{ width: "-webkit-fill-available" }}>
                              <p id="subheader">-field name-</p>
                              <TextField
                                fullWidth
                                disabled={loading}
                                size="small"
                                placeholder="not set"
                                value={field.name}
                                onChange={(event) =>
                                  handleTextFieldChange(index, event, "name")
                                }
                                error={
                                  formik.touched.max_usage &&
                                  Boolean(formik.errors.max_usage)
                                }
                                helperText={
                                  formik.touched.max_usage &&
                                  formik.errors.max_usage
                                }
                                sx={{
                                  "& label": {
                                    paddingLeft: (theme) => theme.spacing(1),
                                  },
                                  "& fieldset": {
                                    borderRadius: "6px",
                                    border: `2px solid #f0f1f5`,
                                    transition: ".2s ease-in-out all",
                                  },
                                  "& .MuiOutlinedInput-root.Mui-disabled .MuiOutlinedInput-notchedOutline":
                                    {
                                      borderColor: "#f0f1f5",
                                    },
                                  "& .MuiOutlinedInput-input:hover": {
                                    cursor: loading && "not-allowed",
                                  },
                                }}
                              />
                            </div>
                            <div style={{ width: "-webkit-fill-available" }}>
                              <p id="subheader">-field value-</p>
                              <TextField
                                fullWidth
                                disabled={loading}
                                size="small"
                                placeholder="not set"
                                value={field.value}
                                onChange={(event) =>
                                  handleTextFieldChange(index, event, "value")
                                }
                                error={
                                  formik.touched.max_usage &&
                                  Boolean(formik.errors.max_usage)
                                }
                                helperText={
                                  formik.touched.max_usage &&
                                  formik.errors.max_usage
                                }
                                sx={{
                                  "& label": {
                                    paddingLeft: (theme) => theme.spacing(1),
                                  },
                                  "& fieldset": {
                                    borderRadius: "6px",
                                    border: `2px solid #f0f1f5`,
                                    transition: ".2s ease-in-out all",
                                  },
                                  "& .MuiOutlinedInput-root.Mui-disabled .MuiOutlinedInput-notchedOutline":
                                    {
                                      borderColor: "#f0f1f5",
                                    },
                                  "& .MuiOutlinedInput-input:hover": {
                                    cursor: loading && "not-allowed",
                                  },
                                }}
                              />
                            </div>
                            <div>
                              <div style={{ height: "18px" }} />
                              <MainButton
                                disabled={loading}
                                color={COLORS.white}
                                textColor={COLORS.black}
                                shadowColor={"transparent"}
                                hoverColor={COLORS.veryLightGrey}
                                textAlignment={"center"}
                                onClickAction={() => deleteOption(index)}
                                width={"fit-content"}
                              >
                                {ICONS.trashBlack20}
                              </MainButton>
                            </div>
                          </div>
                        );
                      })}
                    <div style={{ height: "25px" }} />
                    <MainButton
                      disabled={loading}
                      color={COLORS.white}
                      textColor={COLORS.black}
                      shadowColor={COLORS.black}
                      borderColor={COLORS.black}
                      textAlignment={"center"}
                      onClickAction={() => addOption()}
                      width={"fit-content"}
                    >
                      {loading ? (
                        <>
                          <HashLoader size={20} color={COLORS.grey} /> Add Extra
                          Field
                        </>
                      ) : (
                        "Add Extra Field"
                      )}
                    </MainButton>
                  </div>

                  <div
                    style={{
                      height: "1px",
                      maxHeight: "1px",
                      width: "100%",
                      backgroundColor: "lightgrey",
                      marginBottom: "60px",
                      marginTop: "40px",
                    }}
                  />

                  <div className="field-container">
                    <FormGroup style={{ marginBottom: "20px" }}>
                      <FormControlLabel
                        sx={{
                          "& .MuiTypography-root": {
                            fontFamily: "AuthenticSans90",
                          },
                        }}
                        control={
                          <Checkbox
                            name={"show_pwa_images_block"}
                            checked={formik.values.show_pwa_images_block}
                            onChange={formik.handleChange}
                          />
                        }
                        label="Show images block on LiveSync tickets"
                      />
                      <p style={{ fontSize: "14px", color: "grey" }}>
                        The Images Block is at the bottom of each LiveSync
                        ticket.
                      </p>
                    </FormGroup>
                    <p
                      id="subheader"
                      style={{
                        margin: "5px 0px 3px",
                        alignSelf: "center",
                      }}
                    >
                      Images Block Title
                    </p>
                    <TextField
                      fullWidth
                      size="small"
                      name="pwa_images_block_title"
                      placeholder="Sponsors"
                      value={formik.values.pwa_images_block_title}
                      onChange={formik.handleChange}
                      error={
                        formik.touched.pwa_images_block_title &&
                        Boolean(formik.errors.pwa_images_block_title)
                      }
                      helperText={
                        formik.touched.pwa_images_block_title &&
                        formik.errors.pwa_images_block_title
                      }
                      sx={{
                        marginBottom: "10px",
                        "& label": {
                          paddingLeft: (theme) => theme.spacing(1),
                        },
                        "& fieldset": {
                          borderRadius: "6px",
                          border: `2px solid #f0f1f5`,
                          transition: ".2s ease-in-out all",
                        },
                        "& .MuiOutlinedInput-root.Mui-disabled .MuiOutlinedInput-notchedOutline":
                          {
                            borderColor: "#f0f1f5",
                          },
                        "& .MuiOutlinedInput-input:hover": {
                          cursor: loading && "not-allowed",
                        },
                      }}
                    />
                    {pwaImages.length !== 0 &&
                      pwaImages.map((element, index) => {
                        return (
                          <div
                            key={index}
                            className="two-columns-fields-container"
                            style={{ margin: "5px 0", gap: "10px" }}
                          >
                            {element.url ? (
                              <div className="container-column">
                                <div className="container-row">
                                  <img
                                    src={element.previewUrl || element.url}
                                    style={{
                                      height: "100px",
                                      width: "100%",
                                      objectFit: "cover",
                                      marginBottom: "10px",
                                    }}
                                  />
                                </div>
                                <input
                                  type="file"
                                  className="input-upload"
                                  onChange={(e) =>
                                    handleSelectedPwaImage(e, index)
                                  }
                                  placeholder="Upload image"
                                />
                              </div>
                            ) : (
                              <div
                                className="container-row"
                                style={{
                                  width: "-webkit-fill-available",
                                  justifyContent: "space-between",
                                  alignItems: "center",
                                }}
                              >
                                <input
                                  type="file"
                                  className="input-upload"
                                  onChange={(e) =>
                                    handleSelectedPwaImage(e, index)
                                  }
                                  placeholder="Upload image"
                                />
                              </div>
                            )}
                            <div style={{ width: "-webkit-fill-available" }}>
                              <div style={{ marginBottom: "10px" }}>
                                <p id="subheader">Title</p>
                                <TextField
                                  fullWidth
                                  disabled={loading}
                                  size="small"
                                  placeholder="not set"
                                  value={pwaImages[index].title}
                                  onChange={(event) =>
                                    setPwaImages(
                                      pwaImages.map((image, idx) =>
                                        idx === index
                                          ? {
                                              ...image,
                                              title: event.target.value,
                                            }
                                          : image
                                      )
                                    )
                                  }
                                  error={
                                    formik.touched.max_usage &&
                                    Boolean(formik.errors.max_usage)
                                  }
                                  helperText={
                                    formik.touched.max_usage &&
                                    formik.errors.max_usage
                                  }
                                  sx={{
                                    "& label": {
                                      paddingLeft: (theme) => theme.spacing(1),
                                    },
                                    "& fieldset": {
                                      borderRadius: "6px",
                                      border: `2px solid #f0f1f5`,
                                      transition: ".2s ease-in-out all",
                                    },
                                    "& .MuiOutlinedInput-root.Mui-disabled .MuiOutlinedInput-notchedOutline":
                                      {
                                        borderColor: "#f0f1f5",
                                      },
                                    "& .MuiOutlinedInput-input:hover": {
                                      cursor: loading && "not-allowed",
                                    },
                                  }}
                                />
                              </div>
                              <div>
                                <p id="subheader">Link</p>
                                <TextField
                                  fullWidth
                                  disabled={loading}
                                  size="small"
                                  placeholder="not set"
                                  value={pwaImages[index].link}
                                  onChange={(event) =>
                                    setPwaImages(
                                      pwaImages.map((image, idx) =>
                                        idx === index
                                          ? {
                                              ...image,
                                              link: event.target.value,
                                            }
                                          : image
                                      )
                                    )
                                  }
                                  error={
                                    formik.touched.max_usage &&
                                    Boolean(formik.errors.max_usage)
                                  }
                                  helperText={
                                    formik.touched.max_usage &&
                                    formik.errors.max_usage
                                  }
                                  sx={{
                                    "& label": {
                                      paddingLeft: (theme) => theme.spacing(1),
                                    },
                                    "& fieldset": {
                                      borderRadius: "6px",
                                      border: `2px solid #f0f1f5`,
                                      transition: ".2s ease-in-out all",
                                    },
                                    "& .MuiOutlinedInput-root.Mui-disabled .MuiOutlinedInput-notchedOutline":
                                      {
                                        borderColor: "#f0f1f5",
                                      },
                                    "& .MuiOutlinedInput-input:hover": {
                                      cursor: loading && "not-allowed",
                                    },
                                  }}
                                />
                              </div>
                            </div>
                            <div>
                              <div style={{ height: "18px" }} />
                              <MainButton
                                disabled={loading}
                                color={COLORS.white}
                                textColor={COLORS.black}
                                shadowColor={"transparent"}
                                hoverColor={COLORS.veryLightGrey}
                                textAlignment={"center"}
                                onClickAction={() => deletePwaImage(index)}
                                width={"fit-content"}
                              >
                                {ICONS.trashBlack20}
                              </MainButton>
                            </div>
                          </div>
                        );
                      })}
                    <div style={{ height: "25px" }} />
                    <MainButton
                      disabled={loading}
                      color={COLORS.white}
                      textColor={COLORS.black}
                      shadowColor={COLORS.black}
                      borderColor={COLORS.black}
                      textAlignment={"center"}
                      onClickAction={() => addPwaImage()}
                      width={"fit-content"}
                    >
                      {loading ? (
                        <>
                          <HashLoader size={20} color={COLORS.grey} /> Add Extra
                          Field
                        </>
                      ) : (
                        "Add Image Element"
                      )}
                    </MainButton>
                  </div>

                  <div
                    style={{
                      height: "1px",
                      maxHeight: "1px",
                      width: "100%",
                      backgroundColor: "lightgrey",
                      marginBottom: "60px",
                      marginTop: "40px",
                    }}
                  />

                  <div className="field-container">
                    <p
                      id="subheader"
                      style={{
                        margin: "5px 0px 3px",
                        alignSelf: "center",
                      }}
                    >
                      Terms URL
                    </p>
                    <TextField
                      fullWidth
                      size="small"
                      name="terms"
                      placeholder="not set"
                      value={formik.values.terms}
                      onChange={formik.handleChange}
                      error={
                        formik.touched.terms && Boolean(formik.errors.terms)
                      }
                      helperText={formik.touched.terms && formik.errors.terms}
                      sx={{
                        "& label": {
                          paddingLeft: (theme) => theme.spacing(1),
                        },
                        "& fieldset": {
                          borderRadius: "6px",
                          border: `2px solid #f0f1f5`,
                          transition: ".2s ease-in-out all",
                        },
                        "& .MuiOutlinedInput-root.Mui-disabled .MuiOutlinedInput-notchedOutline":
                          {
                            borderColor: "#f0f1f5",
                          },
                        "& .MuiOutlinedInput-input:hover": {
                          cursor: loading && "not-allowed",
                        },
                      }}
                    />
                  </div>
                  <div className="field-container">
                    <p
                      id="subheader"
                      style={{
                        margin: "5px 0px 3px",
                        alignSelf: "center",
                      }}
                    >
                      Privacy Policy URL
                    </p>
                    <TextField
                      fullWidth
                      size="small"
                      name="privacy_policy"
                      placeholder="not set"
                      value={formik.values.privacy_policy}
                      onChange={formik.handleChange}
                      error={
                        formik.touched.privacy_policy &&
                        Boolean(formik.errors.privacy_policy)
                      }
                      helperText={
                        formik.touched.privacy_policy &&
                        formik.errors.privacy_policy
                      }
                      sx={{
                        "& label": {
                          paddingLeft: (theme) => theme.spacing(1),
                        },
                        "& fieldset": {
                          borderRadius: "6px",
                          border: `2px solid #f0f1f5`,
                          transition: ".2s ease-in-out all",
                        },
                        "& .MuiOutlinedInput-root.Mui-disabled .MuiOutlinedInput-notchedOutline":
                          {
                            borderColor: "#f0f1f5",
                          },
                        "& .MuiOutlinedInput-input:hover": {
                          cursor: loading && "not-allowed",
                        },
                      }}
                    />
                  </div>
                  <div className="field-container">
                    <p
                      id="subheader"
                      style={{
                        margin: "5px 0px 3px",
                        alignSelf: "center",
                      }}
                    >
                      Refund Policy URL
                    </p>
                    <TextField
                      fullWidth
                      size="small"
                      name="refund_policy"
                      placeholder="not set"
                      value={formik.values.refund_policy}
                      onChange={formik.handleChange}
                      error={
                        formik.touched.refund_policy &&
                        Boolean(formik.errors.refund_policy)
                      }
                      helperText={
                        formik.touched.refund_policy &&
                        formik.errors.refund_policy
                      }
                      sx={{
                        "& label": {
                          paddingLeft: (theme) => theme.spacing(1),
                        },
                        "& fieldset": {
                          borderRadius: "6px",
                          border: `2px solid #f0f1f5`,
                          transition: ".2s ease-in-out all",
                        },
                        "& .MuiOutlinedInput-root.Mui-disabled .MuiOutlinedInput-notchedOutline":
                          {
                            borderColor: "#f0f1f5",
                          },
                        "& .MuiOutlinedInput-input:hover": {
                          cursor: loading && "not-allowed",
                        },
                      }}
                    />
                  </div>
                  {/* <div className="field-container">
                    <p
                      id="subheader"
                      style={{
                        margin: "0px 0px 3px",
                        alignSelf: "center",
                      }}
                    >
                      Whitelist - coming soon
                    </p>
                    <TextField
                      fullWidth
                      disabled
                      size="small"
                      name="whitelist"
                      placeholder="not set"
                      value={
                        formik.values.whitelist === null
                          ? ""
                          : formik.values.whitelist
                      }
                      onChange={formik.handleChange}
                      error={
                        formik.touched.whitelist &&
                        Boolean(formik.errors.whitelist)
                      }
                      helperText={
                        formik.touched.whitelist && formik.errors.whitelist
                      }
                      sx={{
                        "& label": {
                          paddingLeft: (theme) => theme.spacing(1),
                        },
                        "& fieldset": {
                          borderRadius: "6px",
                          border: `2px solid #f0f1f5`,
                          transition: ".2s ease-in-out all",
                        },
                        "& .MuiOutlinedInput-root.Mui-disabled .MuiOutlinedInput-notchedOutline":
                          {
                            borderColor: "#f0f1f5",
                          },
                        "& .MuiOutlinedInput-input:hover": {
                          cursor: loading && "not-allowed",
                        },
                      }}
                    />
                  </div> */}
                </>
              )}
              <div
                className="container-row"
                style={{
                  // width: "-webkit-fill-available",
                  alignSelf: "end",
                  justifyContent: "space-between",
                }}
              >
                {/* <MainButton
                  text={
                    loading ? (
                      <>
                        <HashLoader size={20} color={COLORS.grey} /> Delete
                      </>
                    ) : (
                      "Delete"
                    )
                  }
                  width={"fit-content"}
                  disabled={loading}
                  color={COLORS.red}
                  textColor={COLORS.white}
                  textHoverColor={COLORS.lightRed}
                  hoverColor={COLORS.red}
                  shadowColor={COLORS.red}
                  onClickAction={() => handleReset()}
                /> */}
                <div
                  style={{ display: "flex", flexDirection: "row", gap: "5px" }}
                >
                  <MainButton
                    text={
                      loading ? (
                        <>
                          <HashLoader size={20} color={COLORS.grey} /> Reset
                        </>
                      ) : (
                        "Reset"
                      )
                    }
                    disabled={loading}
                    color={"transparent"}
                    textColor={COLORS.red}
                    borderColor={COLORS.red}
                    textHoverColor={COLORS.lightRed}
                    hoverColor={"transparent"}
                    shadowColor={COLORS.red}
                    onClickAction={() => handleReset()}
                  />
                  <MainButton
                    text={
                      loading ? (
                        <>
                          <HashLoader size={20} color={COLORS.grey} /> Save
                        </>
                      ) : (
                        "Save"
                      )
                    }
                    disabled={loading}
                    color={COLORS.black}
                    textColor={COLORS.white}
                    textHoverColor={COLORS.lightGrey}
                    hoverColor={COLORS.black}
                    shadowColor={COLORS.mainAccent}
                    onClickAction={() => formik.handleSubmit()}
                  />
                </div>
              </div>
            </LocalizationProvider>
          </form>
        </div>
      </div>
    </>
  );
};
